import React, { useEffect, useMemo, useState } from 'react';
import DoctorLayout from '../DoctorLayout';
import axios from 'axios';
import { FaClock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import '../../../styles/DoctorOverview.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

// const DECOR_IMG = '/mnt/data/0e86043f-8b91-4ad4-9bb6-80411beed07f.png';

const monthsLabels = ['Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];

const DoctorOverview = () => {
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visiblePatients] = useState(8);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        const token = localStorage.getItem('token');

        const doctorRes = await axios.get(
          'https://iclinc-back.onrender.com/api/v1/doctors/me',
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const patientsRes = await axios.get(
          'https://iclinc-back.onrender.com/api/v1/doctors/myPatients',
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const doctorData = doctorRes.data.data.doctor;
        doctorData.patients = patientsRes.data.data.patients || [];

        setDoctor(doctorData);
      } catch (err) {
        console.error('Error fetching doctor:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctor();
  }, []);

  const appointmentsSeries = useMemo(() => {
    if (!doctor) return monthsLabels.map(() => 0);

    if (Array.isArray(doctor.appointments) && doctor.appointments.length > 0) {
      const counts = monthsLabels.map(() => 0);
      doctor.appointments.forEach((a) => {
        const d = a.date || a.createdAt || a.appointmentDate || a.time;
        if (!d) return;
        const dt = new Date(d);
        if (isNaN(dt)) return;
        const monthIndex = dt.getMonth();
        if (monthIndex >= 0 && monthIndex < counts.length) {
          counts[monthIndex]++;
        }
      });
      if (counts.some((c) => c > 0)) return counts;
    }

    const base = Math.max(1, doctor.patients?.length || 0);
    const series = monthsLabels.map((m, i) =>
      Math.round(
        Math.max(
          2,
          base * (0.5 + (i / monthsLabels.length) * 0.9) +
            (i % 2 === 0 ? base * 0.2 : -base * 0.1)
        )
      )
    );
    return series;
  }, [doctor]);

  if (loading)
    return (
      <div className="d-flex vh-100 justify-content-center align-items-center">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );

  if (!doctor)
    return (
      <div className="d-flex vh-100 justify-content-center align-items-center">
        <div className="text-center">
          <p className="lead">Can't load doctor data, please login first.</p>
        </div>
      </div>
    );

  const lineData = {
    labels: monthsLabels,
    datasets: [
      {
        label: 'Expense',
        data: appointmentsSeries.map((v) => Math.round(v * 0.8)),
        tension: 0.35,
        fill: true,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, 'rgba(14,121,163,0.25)');
          gradient.addColorStop(1, 'rgba(14,121,163,0.02)');
          return gradient;
        },
        borderColor: '#0e79a3',
        pointRadius: 3,
        borderWidth: 2
      },
      {
        label: 'Profit',
        data: appointmentsSeries.map((v) => Math.round(v * 1.15)),
        tension: 0.35,
        fill: false,
        borderColor: '#28a745',
        pointRadius: 2,
        borderWidth: 2
      }
    ]
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1400,
      easing: 'easeOutQuart'
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: { enabled: true, mode: 'index', intersect: false }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#0e79a3' }
      },
      y: {
        grid: { color: '#0e79a338' },
        ticks: { color: '#0e79a3' }
      }
    },
    transition: {
      duration: 800,
      easing: 'easeOutQuart'
    }
  };

  const patientsToShow = doctor.patients || [];

  return (
    <DoctorLayout>
      <div
        className="container-fluid p-4"
        style={{ background: '#e6f6fb', minHeight: '100vh' , borderRadius: '12px'}}
      >
        <div className="d-flex justify-content-between align-items-center mb-3 header-overview-section">
          <div>
            <h1 className="h3 fw-bold" style={{ color: '#0e79a3' }}>
              Inventory Management
            </h1>
            <small className="text-muted">
              Welcome {doctor.fullName.split(' ')[0]} — overview of your
              practice
            </small>
          </div>

          <div className="d-flex align-items-center gap-3">
            <input
              className="form-control"
              placeholder="Search"
              style={{ width: 300, borderRadius: 20 }}
            />
            <button className="btn btn-light clock-btn">
              <FaClock />
            </button>
          </div>
        </div>

        <div className="row g-3 mb-3">
          {/* left sidebar small */}
          <div className="col-md-2">
            <div
              className="card shadow-sm text-center p-3"
              style={{ background: '#0e79a3', color: '#fff', borderRadius: 12 }}
            >
              <img
                src={doctor.photo || '/image 1 (1).png'}
                alt="doc"
                className="rounded-circle mb-2 docImg"
                style={{
                  width: 100,
                  height: 140,
                  objectFit: 'cover',
                  border: '3px solid rgba(11,107,130,0.6)'
                }}
              />
              <div className="fw-bold">{doctor.fullName}</div>
              <small className="text-white-50">{doctor.email}</small>

              <hr style={{ borderColor: '#0e79a333' }} />
            </div>
          </div>

          <div className="col-md-10">
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <div className="card p-3 shadow-sm">
                  <small className="text-muted">Total Patients</small>
                  <div className="h4 fw-bold">
                    {doctor.patients?.length || 0}
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card p-3 shadow-sm">
                  <small className="text-muted">Clinic</small>
                  <div className="h5 fw-bold">{doctor.clinicName || '-'}</div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card p-3 shadow-sm">
                  <small className="text-muted">Rating</small>
                  <div className="h5 fw-bold">⭐ {doctor.rate || 4.2}</div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card p-3 shadow-sm">
                  <small className="text-muted">Upcoming</small>
                  <div className="h5 fw-bold">
                    {doctor.upcomingAppointmentsCount ||
                      Math.max(
                        0,
                        Math.round((doctor.patients?.length || 0) * 0.15)
                      )}
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="card p-3 shadow-sm text-center">
                  <small className="text-muted">Inventory Values</small>
                  <div
                    style={{
                      width: 150,
                      height: 150,
                      borderRadius: '50%',
                      margin: '12px auto',
                      background:
                        'conic-gradient(#0e79a3 0% 32%, #9fc9dc 32% 100%)'
                    }}
                  />
                  <div className="d-flex justify-content-around">
                    <small className="text-muted">
                      <span style={{ color: '#0e79a3' }}>●</span> Sold
                    </small>
                    <small className="text-muted">
                      <span style={{ color: '#9fc9dc' }}>●</span> Total
                    </small>
                  </div>
                </div>
              </div>

              <div className="col-md-5">
                <div className="card p-3 shadow-sm h-100">
                  <small className="text-muted">No of users</small>
                  <div className="h4 fw-bold">
                    {(doctor.patients?.length || 0) > 1000
                      ? Math.round(doctor.patients.length / 1000) + 'K'
                      : doctor.patients?.length}
                  </div>
                  <small className="text-muted">Total Customers</small>
                </div>
              </div>

              <div className="col-md-3">
                <div className="card p-3 shadow-sm">
                  <small className="text-muted">Top Stores</small>
                  <div className="mt-2">
                    {['Gateway Str', 'Rustic Fox', 'Velvet Vine'].map(
                      (s, i) => (
                        <div key={i} className="mb-2">
                          <div className="d-flex justify-content-between">
                            <small className="text-muted">{s}</small>
                            <small className="text-muted">
                              {700 - i * 100}k
                            </small>
                          </div>
                          <div className="progress" style={{ height: 8 }}>
                            <div
                              className="progress-bar bg-info"
                              role="progressbar"
                              style={{ width: `${90 - i * 10}%` }}
                            ></div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="card p-3 shadow-sm mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <small className="text-muted">Expense vs Profit</small>
                  <div className="fw-bold">Last 6 months</div>
                </div>
                <div>
                  <small className="text-muted">Animated</small>
                </div>
              </div>

              <div style={{ height: 260 }}>
                <Line data={lineData} options={lineOptions} />
              </div>
            </div>

            <div className="card shadow-sm p-3 ">
              <div className="d-flex justify-content-between align-items-center mb-3 appointment-listed-card">
                <div>
                  <h5 className="mb-0">Appointments</h5>
                  <small className="text-muted">
                    All appointments & patients — full-width detailed view
                  </small>
                </div>
                <div>
                  <button
                    className="primary-btn btn-sm appointment-btn"
                    onClick={() => navigate('/doctor/appointments')}
                  >
                    Go to Appointments
                  </button>
                </div>
              </div>

              <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                <div className="list-group">
                  {patientsToShow.slice(0, visiblePatients).map((p, idx) => (
                    <div
                      key={idx}
                      className="list-group-item list-group-item-action d-flex gap-3 py-3 listed-patients"
                      aria-current="true"
                      style={{ borderRadius: 10, marginBottom: 8 }}
                    >
                      <img
                        src={p.photo || '/Face.png'}
                        alt={p.name}
                        className="rounded-circle"
                        style={{ width: 72, height: 72, objectFit: 'cover' }}
                      />
                      <div className="d-flex flex-column flex-grow-1">
                        <div className="d-flex justify-content-between">
                          <div>
                            <h6 className="mb-1 fw-bold">{p.name}</h6>
                            <small className="text-muted">{p.email}</small>
                          </div>
                          <div className="text-end">
                            <div className="small text-muted">
                              Status: {p.status || 'Upcoming'}
                            </div>
                            <div className="small text-muted">
                             {p.appointmentTime || p.date || '—'}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 d-flex gap-2">
                          <button className="btn btn-sm btn-outline-success"
                          onClick={() => navigate('/doctor/messages')}
                          >
                            Message
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {patientsToShow.length === 0 && (
                    <div className="text-center p-4 text-muted">
                      No patients / appointments yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DoctorLayout>
  );
};

export default DoctorOverview;
